import { Box, Flex, Text, VStack } from '@chakra-ui/react';

export type TimelineEventStatus = 'completed' | 'current' | 'upcoming';

export interface TimelineEvent {
  label: string;
  actor?: string;
  timestamp?: string;
  description?: string;
  status: TimelineEventStatus;
}

interface ReportStatusTimelineProps {
  events: TimelineEvent[];
}

const MARKER_COLOR: Record<TimelineEventStatus, string> = {
  completed: '#9FB50C',
  current: '#688CC8',
  upcoming: '#CBD5E0',
};

// Purely presentational and data-driven: it only knows how to render whatever
// events it's given (completed / current / upcoming). The actual events come
// from the real report status (SUBMITTED/EDITED) + who/when — no invented
// review/approval stages, since those don't exist in the backend today. If a
// richer workflow is added later, this component already supports an
// 'upcoming' marker for it without changes.
export default function ReportStatusTimeline({ events }: ReportStatusTimelineProps) {
  return (
    <VStack align="stretch" spacing={0}>
      {events.map((event, idx) => {
        const isLast = idx === events.length - 1;
        const isUpcoming = event.status === 'upcoming';
        return (
          <Flex key={idx}>
            <Flex direction="column" align="center" mr={3} flexShrink={0}>
              <Flex
                align="center"
                justify="center"
                w="20px"
                h="20px"
                borderRadius="50%"
                border="2px solid"
                borderColor={MARKER_COLOR[event.status]}
                bg={isUpcoming ? 'white' : MARKER_COLOR[event.status]}
                color={isUpcoming ? MARKER_COLOR[event.status] : 'white'}
                fontSize="10px"
                fontWeight="bold"
                flexShrink={0}
              >
                {event.status === 'completed' ? '✓' : event.status === 'current' ? '●' : ''}
              </Flex>
              {!isLast && <Box w="2px" flex="1" minH="26px" bg={isUpcoming ? 'gray.200' : '#9FB50C'} my={1} />}
            </Flex>
            <Box pb={isLast ? 0 : 5} flex="1" minW={0}>
              <Text fontSize="sm" fontWeight={event.status === 'current' ? 'bold' : 600} color={isUpcoming ? 'gray.400' : 'gray.800'}>
                {event.label}
              </Text>
              {event.actor && (
                <Text fontSize="sm" color="gray.700" mt="2px">
                  {event.actor}
                </Text>
              )}
              {event.timestamp && (
                <Text fontSize="xs" color="gray.500">
                  {event.timestamp}
                </Text>
              )}
              {event.description && (
                <Text fontSize="xs" color="gray.500" mt="2px">
                  {event.description}
                </Text>
              )}
            </Box>
          </Flex>
        );
      })}
    </VStack>
  );
}
